import { GridWrapper } from "@/components/grid-wrapper";
import { useState, useCallback, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import { toast } from "react-toastify";
import { ToastTemplate } from "@/services/variables";
import {
  generateGqlParams,
  generateOptionsDefault,
  getCurrentTimezone,
} from "@/services/generic.methods";
import useRoles from "@/hooks/useRoles";
import { AppointmentColumns } from "@/modules/crm/appointment/constants";
import { useCRMDataClient } from "@/services/data-client/DataClientContext";
import { Get_Appointments } from "@/modules/crm/appointment/graphql-queries";
import { Get_Lead_By_Filters } from "@/modules/crm/leads/graphql-queries/appointment-scheduling";
import dropdownEnums, { FeatureKeys } from "@/services/dropdown-enums";
import { useSelector, useDispatch } from "react-redux";
import {
  getAppointmentType,
  getCalendars,
  getSwitchType,
  getMembers,
  setMembersToState,
  getServiceTypes,
  setAppointmentsToState,
} from "@/store/slices/appointment/appointmentSlice";
import {
  selectGridPagination,
  updateGridPagination,
} from "@/store/slices/grid/gridStateSlice";
import moment from "moment";
import TitleColumn from "./title-column";
import { getTimezones } from "@/store/slices/general/generalSlice";
import { getLoggedinUser } from "@/store/slices/auth/authSlice";
import useFeatures from "@/hooks/useFeatures";
import { knownColsCamel } from "@/components/grid-wrapper/dynamic-columns-generator";

const TableSection = (props) => {
  const { eventActions, appointments, timeZone } = props;
  const calendars = useSelector(getCalendars);
  const serviceTypes = useSelector(getServiceTypes);
  const appointmentTypes = useSelector(getAppointmentType);
  const switchType = useSelector(getSwitchType);
  const [columns, setColumns] = useState([]);
  const permission = useRoles("appointment").getPermission;
  const { client: dataClient } = useCRMDataClient();
  const { hasFeature } = useFeatures();
  const isCrm = hasFeature(FeatureKeys.CRM);
  const user = useSelector(getLoggedinUser);
  const dispatch = useDispatch();
  const storeMembers = useSelector(getMembers);
  const persistedGridPagination = useSelector(
    selectGridPagination("appointments")
  );
  const [leads, setLeads] = useState([]);
  const lastFilterSignatureRef = useRef(null);

  useEffect(() => {
    if (user?.CompanyKey) {
      if (!storeMembers?.length) {
        dataClient.appointments
          .getMembers({ companyKey: user.CompanyKey })
          .then((res) => {
            dispatch(setMembersToState(res?.members?.items || []));
          })
          .catch((err) => console.error("Error fetching members:", err));
      }

      dataClient.leads
        .getGridItems(
          { take: 100 },
          { companyKey: user.CompanyKey, query: Get_Lead_By_Filters }
        )
        .then((res) => {
          setLeads(res?.leadsGrid?.items || []);
        })
        .catch((err) => console.error("Error fetching leads:", err));
    }
  }, [user?.CompanyKey]);

  useEffect(() => {
    // fetch here
  }, [appointments]);

  useEffect(() => {
    const currentSignature = JSON.stringify({
      switchType,
      appointmentTypes: appointmentTypes || [],
    });

    if (lastFilterSignatureRef.current === null) {
      lastFilterSignatureRef.current = currentSignature;
      return;
    }

    if (lastFilterSignatureRef.current !== currentSignature) {
      const currentLimit = persistedGridPagination?.limit || 25;
      dispatch(
        updateGridPagination({
          gridId: "appointments",
          pagination: { skip: 0, limit: currentLimit },
        })
      );
      lastFilterSignatureRef.current = currentSignature;
    }
  }, [dispatch, switchType, JSON.stringify(appointmentTypes)]);

  useEffect(() => {
    const getColumns = AppointmentColumns();
    const cols = getColumns?.map((col) => {
      if (col?.columnName == "serviceType" && calendars?.length) {
        col.dataSource = calendars.map((calendar) => {
          return {
            id: calendar?.id || calendar?.calendarId,
            label: calendar?.name,
          };
        });
      }
      if (col?.columnName == "status") {
        col.dataSource = dropdownEnums.enum_appointmentStatuses.map(
          (status) => {
            let label = status.name;
            if (status.value === 2) {
              label = "Re-Scheduled";
            }
            return {
              id: status.value,
              label: label,
            };
          }
        );
      }
      if (col?.columnName == "leadSource") {
        col.dataSource = dropdownEnums.enum_BasicLeadSource.map((source) => ({
          id: source.value,
          label: source.name,
        }));
      }
      if (col?.columnName == "organizer" && storeMembers?.length) {
        col.dataSource = storeMembers.map((m) => {
          const name = m.name || m.alias || m.email || "Member";
          const id = m.participantId || m.memberId || m.id;
          return {
            id: id,
            label: name,
          };
        });
      }
      if (col?.columnName == "guest" && leads?.length) {
        col.dataSource = leads.map((l) => {
          const name =
            l.firstName && l.lastName
              ? `${l.firstName} ${l.lastName}`
              : l.firstName || l.lastName || l.name || "";
          const email = l.email || "";
          let displayName = "";
          if (name && email) {
            displayName = `${name} (${email})`;
          } else if (name) {
            displayName = name;
          } else if (email) {
            displayName = email;
          } else {
            displayName = "Unknown Lead";
          }
          const id = isCrm ? l.email || l.leadId || l.id : l.leadId || l.id;
          return {
            id: id,
            label: displayName,
          };
        });
      }
      return col;
    });
    setColumns(cols);
  }, [calendars, storeMembers, leads, isCrm]);

  const customCols = [
    {
      columnName: "title",
      render: (e) => <TitleColumn row={e} />,
    },
    {
      columnName: "organizer",
      render: ({ data }) => {
        const resolvedCalendar = calendars.find(
          (c) =>
            c.uuid === data?.calendarId || c.calendarId === data?.calendarId
        );
        const hostMember = resolvedCalendar?.members?.find(
          (m) =>
            m.id === data?.participantId ||
            m.id === data?.memberId ||
            m.memberId === data?.participantId ||
            m.memberId === data?.memberId ||
            m.userId === data?.participantId ||
            m.userId === data?.memberId
        );
        return (
          hostMember?.name ||
          resolvedCalendar?.members?.[0]?.name ||
          data?.organizer ||
          "N/A"
        );
      },
    },
    {
      columnName: "guest",
      render: ({ data }) => {
        return data?.name || data?.guest || data?.email || "N/A";
      },
    },
    {
      columnName: "serviceType",
      render: ({ data }) => {
        const resolvedCalendar = calendars.find(
          (c) =>
            c.uuid === data?.calendarId || c.calendarId === data?.calendarId
        );
        return resolvedCalendar?.name || data?.serviceType || "N/A";
      },
    },
    {
      columnName: "leadSource",
      render: ({ value }) => {
        const isNone = value && String(value).toLowerCase() === "none";
        return isNone || !value ? "N/A" : value;
      },
    },
    {
      columnName: "status",
      render: ({ value }) => {
        let label = "";
        let color = "";
        switch (Number(value)) {
          case 1:
            label = "Scheduled";
            color = "#696A6F";
            break;
          case 2:
            label = "Re-Scheduled";
            color = "#696A6F";
            break;
          case 3:
            label = "Completed";
            color = "#328A36";
            break;
          case 4:
            label = "Canceled";
            color = "#E30606";
            break;
          default:
            const statusInfo = dropdownEnums.enum_appointmentStatuses.find(
              (status) => status.value == value
            );
            label = statusInfo?.name || "";
            color = `var(--palette-${statusInfo?.statusColor || "default"})`;
            break;
        }
        return <span style={{ color, fontWeight: 500 }}>{label}</span>;
      },
    },
  ];

  const timezones = useSelector(getTimezones);

  // const getCurrentTimezone = () => {
  //   const offset = moment().utcOffset();
  //   const hours = Math.floor(Math.abs(offset) / 60);
  //   const minutes = Math.abs(offset) % 60;
  //   const sign = offset >= 0 ? '+' : '-';
  //   const formattedOffset = `(UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')})`;
  //   const currentTz = timezones.find(tz => tz.label.startsWith(formattedOffset));
  //   return currentTz?.value
  // }

  const handleDate = (date) => {
    try {
      const stripped = String(date || "")
        .replace(/\.\d{3}Z$/i, "")
        .replace(/Z$/i, "");
      const wallClock = moment.utc(stripped);
      return wallClock.isValid() ? wallClock : moment(date);
    } catch (error) {
      return moment(date);
    }
  };

  const mapAppointments = (appointments) => {
    const mappedAppointments = appointments?.map((ev) => {
      const handledStart = handleDate(ev.startDate);
      const handledEnd = handleDate(ev.endDate);
      return {
        ...ev,
        start: new Date(handledStart.format("MM/DD/YY HH:mm:ss")),
        end: new Date(handledEnd.format("MM/DD/YY HH:mm:ss")),
        selectedTimeZone: timeZone || getCurrentTimezone(timezones),
      };
    });
    return mappedAppointments;
  };
  const loadData = (props) => {
    try {
      if (appointmentTypes && appointmentTypes.length === 0) {
        dispatch(setAppointmentsToState([]));
        return {
          data: [],
          count: 0,
        };
      }
      // generateGqlParams converts GridWrapper's internal state to { where, skip, take, order }
      const payload = generateGqlParams(props);

      // Inject wide date range for Grid/List View to satisfy the backend requirement
      payload.where = payload.where || {};
      payload.where.and = payload.where.and || [];

      if (isCrm) {
        payload.where.and = payload.where.and.map((filter) => {
          if (filter.guest) {
            return { visitorEmail: filter.guest };
          }
          return filter;
        });
      }

      payload.where.and.push({
        startDate: { gte: moment().subtract(2, "years").toISOString() },
      });
      payload.where.and.push({
        startDate: { lte: moment().add(2, "years").toISOString() },
      });

      // Inject LeftSection filters (Appointment Types / Members selection)
      const totalOptionsCount =
        switchType === "serviceTypes"
          ? serviceTypes?.length
          : storeMembers?.length;
      const memberSelectionFilters = {};
      if (appointmentTypes && appointmentTypes.length > 0) {
        if (appointmentTypes.length < (totalOptionsCount || 1)) {
          if (switchType === "serviceTypes") {
            if (appointmentTypes.length === 1) {
              payload.where.and.push({
                calendarId: { eq: appointmentTypes[0] },
              });
            } else {
              payload.where.and.push({ calendarId: { in: appointmentTypes } });
            }
          } else {
            if (appointmentTypes.length === 1) {
              memberSelectionFilters.memberId = appointmentTypes[0];
              memberSelectionFilters.participantId = appointmentTypes[0];
            } else {
              memberSelectionFilters.memberIds = appointmentTypes;
              memberSelectionFilters.participantIds = appointmentTypes;
            }
          }
        }
      } else if (appointmentTypes && appointmentTypes.length === 0) {
        if (switchType === "serviceTypes") {
          payload.where.and.push({ calendarId: { eq: "none" } });
        } else {
          payload.where.and.push({ memberId: { eq: "none" } });
        }
      }

      // Pass `query` for Pro plan routing.
      // Basic plan uses `where` → SmartHubAppointmentAdapter._extractFilters().
      return dataClient.appointments
        .getAppointments({
          ...payload,
          ...memberSelectionFilters,
          query: Get_Appointments,
          omitBusySlots: true,
          timeZoneId:
            timezones.find((tz) => tz.value === timeZone)?.label || timeZone,
        })
        .then((response) => {
          const appointments = response?.appointments?.items || [];
          const totalCount = Number(response?.appointments?.totalCount || 0);
          dispatch(setAppointmentsToState(appointments));

          return {
            data: mapAppointments(appointments),
            count: totalCount,
          };
        });
    } catch (error) {
      toast.error(ToastTemplate.error(error || error.message));
    }
  };

  const dataSource = useCallback(
    async (props) => {
      console.log(props);

      return await loadData(props);
    },
    [appointmentTypes, switchType, timeZone, timezones]
  );

  return (
    <Box>
      <div className=" mx-3 List-Grid-Container" style={{ height: "100%" }}>
        <Box sx={{ px: 2, pb: 2 }}>
          <GridWrapper
            gridId="appointments"
            // onRowClick={onRowSelect}
            garbageColumnHeaderText={["ID"]}
            rowHeight={54}
            checkboxColumn={false}
            actionButtons={eventActions}
            actionButtonsProps={{ width: 60 }}
            rawColumnsData={columns}
            customColumnsData={customCols}
            // refetch={refetch}
            // selectedData={gridData}
            // handleSelect={handleSelect}
            dataSource={dataSource}
            handleVisibleChange={(e) => console.log(e)}
          ></GridWrapper>
        </Box>
      </div>
    </Box>
  );
};

export default TableSection;
